/**
 * Lightweight Accordion (P2.1-S5) — không add @radix-ui/react-accordion để giảm dep.
 *
 * Mỗi `<AccordionSection>` quản lý own open state (uncontrolled). Nếu cần
 * persist hoặc sync với URL → nâng cấp sau.
 *
 * Doc 33 §5.
 */
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

interface AccordionProps {
  className?: string;
  children: React.ReactNode;
}

export function Accordion({ className, children }: AccordionProps): JSX.Element {
  return (
    <div className={cn("divide-y rounded-md border bg-background", className)}>
      {children}
    </div>
  );
}

interface AccordionSectionProps {
  /** Stable id — dùng cho key + future persist. */
  id?: string;
  title: React.ReactNode;
  /** Optional secondary text bên phải title. */
  subtitle?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  /** Hint icon hoặc badge bên cạnh title. */
  leading?: React.ReactNode;
}

export function AccordionSection({
  title,
  subtitle,
  defaultOpen = false,
  leading,
  children,
}: AccordionSectionProps): JSX.Element {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
      >
        {leading ? <span className="shrink-0">{leading}</span> : null}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-foreground">
            {title}
          </div>
          {subtitle ? (
            <div className="text-[10px] text-muted-foreground truncate">
              {subtitle}
            </div>
          ) : null}
        </div>
        <ChevronDown
          className={cn(
            "size-4 text-muted-foreground transition-transform shrink-0",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? (
        <div className="px-3 py-3 border-t bg-muted/10 animate-fade-in">
          {children}
        </div>
      ) : null}
    </div>
  );
}
